<template>
    <div class="page-tournaments__list" :class="[`${reference}-tournament`, {load: load}]">
        <div class="page-tournaments__list-title" v-if="!isMobile || isMobile && active === reference">
            <h3>{{ mainTitle }}</h3>
            <div class="line"></div>
        </div>

        <div class="page-tournaments__list-empty"
             v-if="(!isMobile || isMobile && active === reference) && !items.length">
            <h3>{{ emptyTitle }}</h3>
            <div></div>
            <p>{{ $t('front.tournament.empty.description') }}</p>
        </div>

        <div class="page-tournaments__swiper-items" v-show="isMobile && active === reference">
            <swiper ref="tournaments" :options="settings">
                <swiper-slide class="item" v-for="(item, index) in items" :key="index">
                    <div class="item__image" :class="reference"
                         :style="`background-image: linear-gradient(black, black), url(${tournamentBackground(item)})`">
                        <img :src="tournamentImage(item)" :alt="tournamentTitle(item)">
                        <div class="badge private" v-if="item.type.toLowerCase() === 'private'">
                            {{ $t('front.tournament.title.private') }}
                        </div>
                    </div>
                    <div class="item__content">
                        <div class="info" @click="open(item.games)"></div>
                        <div class="badge" :class="item.status">{{ $t(`front.tournament.status.${item.status}`) }}</div>
                        <div class="title">{{ tournamentTitle(item) }}</div>

                        <div class="date">
                            <div class="title">{{ dateTitle }}</div>
                            <div class="value" v-if="checkDate">
                                <countdown-timer :date="tournamentDate(item)"/>
                            </div>
                            <div class="value" v-else>
                                {{ formattedDate(item.endDate) }}
                            </div>
                        </div>

                        <div class="prize" v-if="award(item, 1)">
                            <div class="title">{{ $t('front.tournament.title.award') }}</div>
                            <div class="value">
                                <i class="icon"></i>
                                {{ award(item, 1) }}
                            </div>
                        </div>

                        <v-btn v-on:click="tournamentUrl(item)">
                            {{ buttonLabel(item) }}
                        </v-btn>
                    </div>
                </swiper-slide>
            </swiper>

            <div class="swiper-navigation">
                <v-btn v-on:click="slidePrev" class="prev">
                    <svg width="11" height="10" viewBox="0 0 11 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 1L2 5L6 9" stroke="white" stroke-width="1.5"/>
                        <path d="M2.5 5H10.5" stroke="white" stroke-width="1.5"/>
                    </svg>
                </v-btn>

                <div class="pagination">
                    <span v-for="index in items.length" :key="index"
                          v-on:click="slideTo(index)"
                          :class="{active: activeSlide === (index - 1)}">
                    </span>
                </div>

                <v-btn v-on:click="slideNext" class="next">
                    <svg width="11" height="10" viewBox="0 0 11 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5 1L9 5L5 9" stroke="white" stroke-width="1.5"/>
                        <path d="M8.5 5H0.5" stroke="white" stroke-width="1.5"/>
                    </svg>
                </v-btn>
            </div>
        </div>

        <div class="page-tournaments__list-items" v-show="!isMobile">
            <div class="item" :ref="reference" v-for="(item, index) in items" :key="index">
                <div class="item__image" :class="reference"
                     :style="`background-image: ${reference === 'finished' ? 'linear-gradient(black, black),' : ''} url(${tournamentBackground(item)})`">
                    <img :src="tournamentImage(item)" :alt="tournamentTitle(item)">
                    <div class="badge private" v-if="item.type.toLowerCase() === 'private'">
                        {{ $t('front.tournament.title.private') }}
                    </div>
                    <div class="info">
                        <div>
                            {{ $t('front.tournament.title.length') }}
                            <strong>{{ tournamentLength(item) }}</strong>
                        </div>

                        <div v-if="item.spinLimit">
                            <span>|</span>
                            {{ $t('front.tournament.title.spins') }}
                            <strong>{{ parseInt(item.spinLimit) }}</strong>
                        </div>

                        <div v-if="item.minBetAmount">
                            <span>|</span>
                            {{ $t('front.tournament.title.min_bet') }}
                            <strong>{{ item.minBetAmount }} {{ item.currency }}</strong>
                        </div>
                    </div>
                </div>
                <div class="item__content">
                    <div class="info" @click="open(item.games)"></div>
                    <div class="badge" :class="item.status">{{ $t(`front.tournament.status.${item.status}`) }}</div>
                    <div class="title">{{ tournamentTitle(item) }}</div>

                    <div class="date">
                        <div class="title">{{ dateTitle }}</div>
                        <div class="value" v-if="checkDate">
                            <countdown-timer :date="tournamentDate(item)"/>
                        </div>
                        <div class="value" v-else>
                            {{ formattedDate(item.endDate) }}
                        </div>
                    </div>

                    <div class="prize" v-if="award(item, 1)">
                        <div class="title">{{ $t('front.tournament.title.award') }}</div>
                        <div class="value">
                            <i class="icon"></i>
                            {{ award(item, 1) }}
                        </div>
                    </div>

                    <v-btn v-on:click="tournamentUrl(item)">
                        {{ buttonLabel(item) }}
                    </v-btn>
                </div>
            </div>
        </div>

        <v-dialog v-model="dialog" :persistent="isDialogPersistent" max-width="600" scrollable>
            <tournament-related-games @close="close" :related-games="dialogItem"/>
        </v-dialog>
    </div>
</template>

<script>
import {Swiper, SwiperSlide} from "vue-awesome-swiper";
import DialogSettings from "../../mixins/dialogSettings";
import apiData from "../../mixins/apiData";
import {formatStringDateToDate} from "../../util/date-format";

export default {
    name: "TournamentPart",

    components: {
        Swiper,
        SwiperSlide,
    },

    mixins: [apiData, DialogSettings],

    props: ['reference', 'active', 'items', 'priority', 'delay'],

    data: function () {
        return {
            dialog: false,
            dialogItem: undefined,
            isMobile: true,
            activeSlide: 0,
            timeout: undefined,
            load: false,
            delayOn: 500,
            settings: {
                init: true,
                loop: true,
                slidesPerView: 1,
                direction: 'horizontal',
                spaceBetween: 10,
                speed: 500,
            },
        }
    },

    mounted() {
        this.fixWrapped();
        window.addEventListener('resize', this.fixWrapped);
        window.addEventListener('orientationchange', this.fixWrapped);

        this.$refs.tournaments.$swiper.on('slideChange', (s) => {
            this.activeSlide = s.realIndex;
        });
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.fixWrapped);
        window.removeEventListener('orientationchange', this.fixWrapped);
    },

    watch: {},

    computed: {
        locale() {
            return this.$store.state.locale;
        },

        mainTitle() {
            switch (this.reference) {
                case 'live':
                    return `${this.$t('front.tournament.count.title.live')} (${this.items.length})`;
                case 'upcoming':
                    return `${this.$t('front.tournament.count.title.upcoming')} (${this.items.length})`;
                case 'finished':
                    return `${this.$t('front.tournament.count.title.finished')} (${this.items.length})`;
                default:
                    return '';
            }
        },

        emptyTitle() {
            switch (this.reference) {
                case 'live':
                    return this.$t('front.tournament.empty.title.live');
                case 'upcoming':
                    return this.$t('front.tournament.empty.title.upcoming');
                case 'finished':
                    return this.$t('front.tournament.empty.title.finished');
                default:
                    return '';
            }
        },

        dateTitle() {
            switch (this.reference) {
                case 'live':
                    return this.$t('front.tournament.title.ends_in');
                case 'upcoming':
                    return this.$t('front.tournament.title.start_in');
                case 'finished':
                    return this.$t('front.tournament.title.finished_on');
                default:
                    return '';
            }
        },

        checkDate() {
            return this.reference === 'live' || this.reference === 'upcoming';
        },
    },

    methods: {
        fixWrapped() {
            this.isMobile = window.innerWidth <= 800;

            if (this.isMobile) {
                this.load = true;
                return;
            }

            let priority = this.priority * 200,
                delay = priority + (this.delay ? this.delayOn : 0),
                ref = this.$refs.live && this.$refs.live[0] && this.$refs.live ||
                    this.$refs.upcoming && this.$refs.upcoming[0] && this.$refs.upcoming ||
                    this.$refs.finished && this.$refs.finished[0] && this.$refs.finished;

            ref && setTimeout(() => this.fix(ref), priority);
            setTimeout(() => this.load = true, delay);
        },

        fix(list) {
            let first = list[0],
                count = list.length;

            if (count < 3) {
                list.forEach(el => {
                    if (!el.classList.contains('wrapped')) {
                        el.classList.add('wrapped');
                    }
                });

                return;
            }

            list.forEach(el => {
                if (el.classList.contains('wrapped')) {
                    el.classList.remove('wrapped');
                }
            });

            clearTimeout(this.timeout);

            this.timeout = setTimeout(() => {
                let top = first.getBoundingClientRect().y;

                list.forEach(el => {
                    if (top !== el.getBoundingClientRect().y) {
                        el.classList.add('wrapped');
                    }
                });

            }, this.delayOn);
        },

        tournamentTitle(item) {
            return item.title;
        },

        tournamentImage(item) {
            return item && item.image2;
        },

        tournamentBackground(item) {
            return item && item.image;
        },

        tournamentLength(item) {
            let startDate = new Date(item.startDate),
                endDate = new Date(item.endDate),
                dateDiff = endDate - startDate,
                diff = Math.abs(dateDiff / 1000),
                days = Math.floor(diff / 24 / 60 / 60),
                hoursLeft = Math.floor((diff) - (days * 86400)),
                hours = Math.floor(hoursLeft / 3600),
                minutesLeft = Math.floor((hoursLeft) - (hours * 3600)),
                minutes = Math.floor(minutesLeft / 60),
                seconds = Math.floor(diff % 60);

            if (days) return `${days} ${this.$t('front.tournament.title.days')}`;
            if (hours) return `${hours} ${this.$t('front.tournament.title.hours')}`;
            if (minutes) return `${minutes} ${this.$t('front.tournament.title.minutes')}`;

            return `${seconds} ${this.$t('front.tournament.title.seconds')}`;
        },

        tournamentDate(item) {
            if (this.reference === 'live') {
                return item.endDate;
            }

            return item.startDate;
        },

        formattedDate(date) {
            return formatStringDateToDate(date, 'YYYY-MM-DD', this.locale);
        },

        tournamentUrl(item) {
            let languageUrlPrefix = this.$store.getters.getLanguageUrlPrefix(),
                url = item.pageUrl || '';

            url && `${languageUrlPrefix}${url}` && this.$router.push(`${languageUrlPrefix}${url}`);
        },

        buttonLabel(item) {
            if (item.status === 'active') {
                return this.$t('front.tournament.button.play_now');
            }

            return this.$t('front.tournament.button.view');
        },

        award(item, rank) {
            let award = item.awards && Object.values(item.awards)
                .find((award) => award['rank'] === rank);

            return award && award.title || '';
        },

        slidePrev() {
            this.$refs.tournaments.$swiper.slidePrev();
        },

        slideNext() {
            this.$refs.tournaments.$swiper.slideNext();
        },

        slideTo(index) {
            this.$refs.tournaments.$swiper.slideTo(index);
        },

        open(item) {
            this.dialogItem = item;
            this.dialog = true;
        },

        close() {
            this.dialog = false;
        },
    },
}
</script>